import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Tracks which product tours a user has already seen, so each page-level tour
 * auto-runs only on the first visit. Persisted per device (mirrors the prefs
 * store). Bump `version` if the tour catalogue changes meaningfully and you
 * want everyone to see the refreshed walkthroughs again.
 */
interface TourState {
  /** tourId → true once the user finished or dismissed that tour. */
  seen: Record<string, boolean>;
  /** Global opt-out — when true, no tour auto-starts (manual launch still works). */
  autoplayDisabled: boolean;
  hasSeen: (tourId: string) => boolean;
  markSeen: (tourId: string) => void;
  setAutoplayDisabled: (disabled: boolean) => void;
  /** Clear all "seen" flags so every page tour plays again. */
  resetAll: () => void;
}

export const useTourStore = create<TourState>()(
  persist(
    (set, get) => ({
      seen: {},
      autoplayDisabled: false,
      hasSeen: (tourId) => Boolean(get().seen[tourId]),
      markSeen: (tourId) => set((s) => ({ seen: { ...s.seen, [tourId]: true } })),
      setAutoplayDisabled: (autoplayDisabled) => set({ autoplayDisabled }),
      resetAll: () => set({ seen: {} }),
    }),
    { name: 'xotski.tours', version: 1 },
  ),
);
